import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAudit } from "../context/AuditContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import API from "../lib/api";
import { Folder, Upload, ArrowRight, FileText, Loader2, Download, ExternalLink, Trash2 } from "lucide-react";

export default function Phase5Artifacts() {
  const navigate = useNavigate();
  const { auditRun, advancePhase, refreshRun } = useAudit();
  const [artifacts, setArtifacts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [availableDownloads, setAvailableDownloads] = useState([]);
  const [transactionSummary, setTransactionSummary] = useState(null);
  const [transactionJobState, setTransactionJobState] = useState(null);
  const transactionDataset = auditRun?.transaction_dataset;

  const fetchArtifacts = useCallback(async () => {
    if (!auditRun?.id) return;
    const { data } = await API.get(`/audit-runs/${auditRun.id}/artifacts`);
    setArtifacts(data);
  }, [auditRun?.id]);

  const fetchAvailableDownloads = useCallback(async () => {
    try {
      const { data } = await API.get('/available-downloads');
      setAvailableDownloads(data.downloads || []);
    } catch (err) {
      console.error('Failed to fetch downloads:', err);
    }
  }, []);

  useEffect(() => { fetchArtifacts(); fetchAvailableDownloads(); }, [fetchArtifacts, fetchAvailableDownloads]);

  const fetchTransactionSummary = useCallback(async () => {
    if (!auditRun?.id) return null;
    try {
      const { data } = await API.get(`/audit-runs/${auditRun.id}/transactions/summary`);
      setTransactionSummary(data);
      return data;
    } catch (err) {
      console.error("Failed to fetch transaction summary:", err);
      return null;
    }
  }, [auditRun?.id]);

  useEffect(() => {
    fetchTransactionSummary();
  }, [fetchTransactionSummary, transactionDataset?.status]);

  useEffect(() => {
    const jobId = transactionDataset?.job_id;
    if (!auditRun?.id || !jobId || transactionDataset?.status !== "ingesting") return undefined;

    let cancelled = false;

    const poll = async () => {
      try {
        const [{ data: status }] = await Promise.all([
          API.get(`/job-status/${jobId}`),
          refreshRun(),
          fetchTransactionSummary(),
        ]);
        if (cancelled) return;
        setTransactionJobState(status);
        if (status.ready) return;
        setTimeout(poll, 2000);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to poll transaction ingestion status:", err);
        }
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [auditRun?.id, transactionDataset, fetchTransactionSummary, refreshRun]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !auditRun?.id) return;
    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("doc_type", "artifact");
      try {
        await API.post(`/audit-runs/${auditRun.id}/artifacts`, formData, { headers: { "Content-Type": "multipart/form-data" } });
        toast.success(`Uploaded: ${file.name}`);
      } catch (err) { toast.error(`Failed: ${file.name}`); }
    }
    setUploading(false);
    await fetchArtifacts();
    e.target.value = "";
  };

  const handleContinue = async () => {
    await advancePhase(6);
    navigate("/phase/6");
    toast.success("Proceeding to Sampling Strategy");
  };

  const handleDelete = async (artifactId, filename) => {
    if (!auditRun?.id) return;
    try {
      const { data } = await API.delete(`/audit-runs/${auditRun.id}/artifacts/${artifactId}`);
      toast.success(data?.deleted_rows
        ? `Deleted: ${filename} (${data.deleted_rows} rows removed)`
        : `Deleted: ${filename}`);
      await Promise.all([fetchArtifacts(), fetchTransactionSummary(), refreshRun()]);
    } catch (err) {
      toast.error("Failed to delete artifact");
    }
  };

  return (
    <div className="space-y-6" data-testid="phase-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Phase 5: Artifact Collection</h2>
          <p className="text-sm text-muted-foreground mt-1">Upload evidence files — transaction logs, tabular data exports (XLSX/CSV), case files, system access logs, and screenshots.</p>
        </div>
        <Button onClick={handleContinue} data-testid="continue-to-sampling-top-btn" className="sm:shrink-0">
          <ArrowRight className="w-4 h-4 mr-2" /> Continue to Sampling
        </Button>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base">Upload Artifacts</CardTitle></CardHeader>
        <CardContent>
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center hover:border-primary/50 transition-colors">
            <label className="cursor-pointer block">
              {uploading ? <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin text-muted-foreground" /> : <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />}
              <p className="text-sm font-medium">{uploading ? "Uploading..." : "Click to upload evidence files"}</p>
              <p className="text-xs text-muted-foreground mt-1">PDF &middot; XLSX &middot; CSV &middot; images</p>
              <p className="text-xs text-muted-foreground mt-0.5">Orders, Delivery Partners, Access Logs, Incidents, Refunds, Coupons</p>
              <input type="file" multiple onChange={handleUpload} className="hidden"
                accept=".pdf,.xlsx,.xls,.csv,.docx,.doc,.txt,.md,.png,.jpg,.jpeg"
                data-testid="artifact-upload-input"
                disabled={!auditRun || uploading} />
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Sample Artifacts Download Section */}
      {availableDownloads.length > 0 && (
        <Card className="border-dashed">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Download className="w-4 h-4" /> Sample Artifact Packages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-muted-foreground mb-3">
              Download pre-built evidence packages to test the audit workflow. Extract the ZIP and upload relevant files above.
            </p>
            <div className="grid gap-2">
              {availableDownloads.map((dl, idx) => (
                <a
                  key={idx}
                  href={`${process.env.REACT_APP_BACKEND_URL}${dl.url}`}
                  download
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/50 transition-colors group"
                  data-testid={`download-${dl.filename}`}
                >
                  <div className="flex items-center gap-3">
                    <Folder className="w-4 h-4 text-muted-foreground" />
                    <div>
                      <p className="text-sm font-medium">{dl.filename}</p>
                      <p className="text-xs text-muted-foreground">{dl.size_kb} KB</p>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
                </a>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* {auditRun?.transaction_dataset && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Structured Dataset Ingestion</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Filename</p>
                <p className="font-mono text-xs mt-1 break-all">{auditRun.transaction_dataset.filename || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Dataset Type</p>
                <p className="mt-1">{auditRun.transaction_dataset.dataset_type || "-"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Status</p>
                <div className="mt-1 flex items-center gap-2">
                  <div className={`status-dot ${auditRun.transaction_dataset.status === 'ready' ? 'status-ready' : auditRun.transaction_dataset.status === 'failed' ? 'bg-red-500' : 'status-uploaded'}`} />
                  <span className="font-mono text-xs capitalize">{auditRun.transaction_dataset.status || "unknown"}</span>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Rows</p>
                <p className="mt-1 font-mono text-xs">{auditRun.transaction_dataset.row_count ?? transactionSummary?.total_records ?? 0}</p>
              </div>
            </div>
            {transactionJobState?.state === "PROGRESS" && (
              <p className="text-xs text-muted-foreground">
                Ingesting {transactionJobState.meta?.rows_inserted ?? 0} rows so far from GridFS.
              </p>
            )}
            {auditRun.transaction_dataset.error && (
              <p className="text-xs text-red-500">{auditRun.transaction_dataset.error}</p>
            )}
            {transactionSummary?.by_dataset_type?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {transactionSummary.by_dataset_type.map((item) => (
                  <Badge key={item.dataset_type} variant="outline" className="text-xs">
                    {item.dataset_type}: {item.count}
                  </Badge>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )} */}

      {artifacts.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="text-base">Collected Artifacts ({artifacts.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table className="table-dense">
              <TableHeader>
                <TableRow>
                  <TableHead>Filename</TableHead>
                  <TableHead>Classification</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Size</TableHead>
                  <TableHead className="w-[60px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {artifacts.map(a => (
                  <TableRow key={a.id}>
                    <TableCell className="font-mono text-xs flex items-center gap-2">
                      <FileText className="w-3 h-3 text-muted-foreground shrink-0" /> {a.filename}
                    </TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{a.classification || 'Artifact'}</Badge></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className={`status-dot ${a.status === 'ready' ? 'status-ready' : 'status-uploaded'}`} />
                        <span className="text-xs font-mono capitalize">{a.status}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right font-mono text-xs">
                      {a.file_size ? `${(a.file_size / 1024).toFixed(1)}KB` : '-'}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                        onClick={() => handleDelete(a.id, a.filename)}
                        title="Delete artifact"
                        data-testid={`delete-artifact-${a.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end">
        <Button onClick={handleContinue} data-testid="continue-to-sampling-btn">
          <ArrowRight className="w-4 h-4 mr-2" /> Continue to Sampling
        </Button>
      </div>
    </div>
  );
}
